stimulusApp.register(
  "transfer-guest-stepper",
  class extends Stimulus.Controller {
    static targets = [
      "input",
      "decrementButton",
      "incrementButton",
      "syncInput",
      "transferOption",
      "capacityCaption",
    ]
    static values = { min: Number, max: Number, prices: Object }

    connect() {
      this.#update()
    }

    decrement(event) {
      event.preventDefault()
      if (this.inputTarget.disabled) return

      this.#setValue(this.inputTarget.valueAsNumber - 1)
    }

    increment(event) {
      event.preventDefault()
      if (this.inputTarget.disabled) return

      this.#setValue(this.inputTarget.valueAsNumber + 1)
    }

    #setValue(value) {
      const nextValue = this.#clamp(value)
      if (this.inputTarget.valueAsNumber === nextValue) {
        this.#update()
        return
      }

      this.inputTarget.value = nextValue
      this.#update()
      this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }

    #update() {
      const value = this.#clamp(this.inputTarget.valueAsNumber)
      this.inputTarget.value = value
      this.decrementButtonTarget.disabled =
        this.inputTarget.disabled || value <= this.#minimumValue()
      this.incrementButtonTarget.disabled =
        this.inputTarget.disabled || value >= this.#maximumValue()
      this.#syncInputs(value)
      this.#updateTransferOptions(value)
      this.#updatePrice(value)
    }

    #clamp(value) {
      const minimumValue = this.#minimumValue()
      if (Number.isNaN(value)) return minimumValue

      const minValue = Math.max(value, minimumValue)

      return Math.min(minValue, this.#maximumValue())
    }

    #updatePrice(value) {
      const prices = this.#prices()
      if (!prices) return

      Object.entries(prices).forEach(([transferId, transferPrices]) => {
        if (typeof transferPrices !== "object") return

        const content = transferPrices[String(value)]
        if (content) {
          this.#updatePriceContainer(transferId, content)
        }
      })
    }

    #syncInputs(value) {
      this.syncInputTargets.forEach((input) => {
        input.value = value
      })
    }

    #updateTransferOptions(value) {
      this.transferOptionTargets.forEach((option) => {
        const maxGuests = Number(option.dataset.transferGuestStepperMaxGuests)
        const capacityExceeded = Boolean(maxGuests && value > maxGuests)
        const locked = option.dataset.transferGuestStepperLocked === "true"
        const uncheckedForCapacity = capacityExceeded && option.checked

        if (uncheckedForCapacity) {
          option.checked = false
        }

        option.disabled = locked || capacityExceeded
        this.#updateCapacityCaption(option, capacityExceeded)

        if (uncheckedForCapacity) {
          option.dispatchEvent(new Event("change", { bubbles: true }))
        }
      })
    }

    #updateCapacityCaption(option, capacityExceeded) {
      const transferId = option.dataset.transferGuestStepperTransferId
      if (!transferId) return

      this.capacityCaptionTargets
        .filter(
          (caption) =>
            caption.dataset.transferGuestStepperTransferId === transferId,
        )
        .forEach((caption) => {
          caption.classList.toggle(
            "transfer-guest-stepper--caption_exceeded",
            capacityExceeded,
          )
        })
    }

    #updatePriceContainer(transferId, content) {
      this.element
        .querySelectorAll(`[data-transfer-price-id="${transferId}"]`)
        .forEach((element) => {
          element.innerHTML = content
        })
    }

    #minimumValue() {
      const value = Number(
        this.#configurationElement()?.dataset.transferGuestStepperMinValue,
      )

      return Number.isNaN(value) ? 1 : value
    }

    #maximumValue() {
      const value = Number(
        this.#configurationElement()?.dataset.transferGuestStepperMaxValue,
      )

      return Number.isNaN(value) ? Infinity : value
    }

    #prices() {
      if (this.hasPricesValue) return this.pricesValue

      const rawPrices =
        this.#configurationElement()?.dataset.transferGuestStepperPricesValue
      if (!rawPrices) return null

      return JSON.parse(rawPrices)
    }

    #configurationElement() {
      return (
        this.element.querySelector("[data-transfer-guest-stepper-min-value]") ||
        this.element
      )
    }
  },
)
